import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, BookOpen, PlayCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface WeakAreasPanelProps {
  weakAreas: Array<{
    tag: string;
    accuracy: number;
    attempts: number;
    avgTimeSeconds: number;
  }>;
  difficultyIssues: Array<{
    difficulty: number;
    accuracy: number;
    attempts: number;
  }>;
  commonMistakes: Array<{
    mcqId: string;
    stem: string;
    attemptCount: number;
    failureRate: number;
  }>;
  topicId: string;
}

export function WeakAreasPanel({ 
  weakAreas, 
  difficultyIssues, 
  commonMistakes,
  topicId 
}: WeakAreasPanelProps) {
  const navigate = useNavigate();

  const difficultyLabels: Record<number, string> = {
    1: 'Very Easy',
    2: 'Easy',
    3: 'Medium',
    4: 'Hard',
    5: 'Very Hard',
  };

  const handlePracticeWeakAreas = () => {
    const weakTags = weakAreas.map(w => w.tag).join(',');
    navigate(`/practice?topic=${topicId}&tags=${weakTags}`);
  };

  if (weakAreas.length === 0 && difficultyIssues.length === 0 && commonMistakes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-green-500" />
            No Weak Areas Identified
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Great job! You're performing well across all areas of this topic. Keep up the good work!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Weak Topic Areas */}
      {weakAreas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Weak Topic Areas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {weakAreas.slice(0, 5).map((area) => (
              <div key={area.tag} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold">{area.tag}</h3>
                    <p className="text-sm text-muted-foreground">
                      {area.accuracy}% accuracy ({area.attempts} attempts)
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-red-500">
                    {area.accuracy}%
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Average time: {area.avgTimeSeconds}s per question
                </p>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => navigate(`/practice?topic=${topicId}&tags=${area.tag}`)}
                  >
                    <PlayCircle className="mr-2 h-4 w-4" />
                    Practice Now
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => navigate(`/study/${topicId}`)}
                  >
                    <BookOpen className="mr-2 h-4 w-4" />
                    Review Notes
                  </Button>
                </div>
              </div>
            ))}
            {weakAreas.length > 0 && (
              <Button 
                className="w-full" 
                onClick={handlePracticeWeakAreas}
              >
                Practice All Weak Areas
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Difficulty Issues */}
      {difficultyIssues.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Difficulty Level Challenges
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {difficultyIssues.map((issue) => (
              <div key={issue.difficulty} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold">
                      {difficultyLabels[issue.difficulty]} Questions
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {issue.attempts} questions attempted
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-orange-500">
                    {issue.accuracy}%
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Focus on building a stronger foundation before tackling harder questions
                </p>
                <Button 
                  size="sm" 
                  onClick={() => navigate(`/practice?topic=${topicId}&difficulty=${issue.difficulty}`)}
                >
                  <PlayCircle className="mr-2 h-4 w-4" />
                  Focused Practice
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Common Mistakes */}
      {commonMistakes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Frequently Missed Questions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {commonMistakes.map((mistake, index) => (
                <div key={mistake.mcqId} className="border rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium line-clamp-2">
                        {index + 1}. {mistake.stem}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Failed {mistake.failureRate}% of {mistake.attemptCount} attempts
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => navigate(`/practice?mcq=${mistake.mcqId}`)}
                  >
                    Review Question
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
